import style from './index.module.css'

import React, { useState } from 'react'

import { getNativeTags } from '@disco/state-management/selectors/tags'

import { FilterComponentProps } from 'types/filter-page'
import type { NativeTag, NativeTagWithCategory } from 'types/track'

import { Flex, Separator, Text, TextField } from 'components/ds'
import Accordion from 'components/ui/accordion'

import { useSelector } from 'hooks/use-redux'

import TagSearchTextField from './filter-search'
import SelectedTags from './filter-search/selected-tags'
import TagsCheckbox from './tag-checkbox'
import {
  getCategoriesAndTags,
  getFilteredSelectedTags,
  getMatchingTagCategories,
} from './utils'

const TagsForm = ({
  value,
  onChange,
  label,
  operator = 'equals',
}: FilterComponentProps) => {
  const nativeTags: NativeTagWithCategory[] = useSelector(getNativeTags)
  const [searchValue, setSearchValue] = useState<string>('')
  const [isAccordionOpen, setIsAccordionOpen] = useState<string[]>([])
  const [visibleTags, setVisibleTags] =
    useState<NativeTagWithCategory[]>(nativeTags)

  const handleSearchFilters = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchInput = event.target.value.toLowerCase().trim()
    if (!searchInput) {
      setIsAccordionOpen([])
      setSearchValue('')
      setVisibleTags(nativeTags)
      return
    }
    const filteredTags = nativeTags.filter(tag =>
      tag.name.toLowerCase().includes(searchInput),
    )
    const matchingTagCategories = getMatchingTagCategories(
      searchInput,
      filteredTags,
    )
    setSearchValue(event.target.value)
    setVisibleTags(filteredTags)
    setIsAccordionOpen(matchingTagCategories)
  }

  const handleTagCheck = (checked: string | boolean, tag: NativeTag) => {
    const selectedTagIds = checked
      ? [...(value ?? []), tag.id]
      : (value ?? []).filter((id: number) => id !== tag.id)
    onChange(selectedTagIds?.length ? selectedTagIds : undefined, operator)
  }

  const tagGroups = getCategoriesAndTags(visibleTags, value)
  const selectedTagGroups = getFilteredSelectedTags(
    value,
    searchValue,
    nativeTags,
  )
  const isSeparatorVisible =
    selectedTagGroups.length > 0 && Object.keys(tagGroups).length > 0

  const accordionItems = Object.entries(tagGroups).map(
    ([categoryName, tags]) => ({
      id: categoryName,
      title: categoryName,
      content: (
        <TagsCheckbox
          tagOptions={tags}
          onCheckChange={handleTagCheck}
          value={value}
          searchValue={searchValue}
        />
      ),
    }),
  )

  return (
    <Flex direction="column" width="100%" position="relative">
      <TagSearchTextField
        searchValue={searchValue}
        onSearch={handleSearchFilters}
      />
      <Flex direction="column" width="100%">
        <SelectedTags
          selectedTags={selectedTagGroups}
          onRemoveTag={(tagIds, operator) =>
            onChange(tagIds?.length ? tagIds : undefined, operator)
          }
          searchValue={searchValue}
        />
        {isSeparatorVisible && <Separator size="4" my="2" />}
        <Flex direction="column">
          {accordionItems.map(item => (
            <Accordion
              key={item.id}
              title={<Text size="2">{item.title}</Text>}
              arrowType="arrow-down"
              scrollIntoView={false}
              titleClassName={style.Tag_AccordionTitle}
              contentClassName={style.Tag_AccordionContent}
              adjustedContentHeight="min-content"
              expanded={isAccordionOpen.includes(item.title)}
              onTitleClick={() => setIsAccordionOpen([item.title])}
            >
              {item.content}
            </Accordion>
          ))}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default TagsForm
